import { useGetPartnerQuery, usePutPartnerMutation } from '@/api/api.slice.tsx';
import { PartnerUpdateType } from '@/api/types.ts';
import { Logger } from '@arsnovasystems/ars-front-lib';
import { useCallback } from 'react';

/**
 * This hook is made to centralize the logic to update a partner.
 *
 * @returns resultObject object with result props : isLoading, savePartner and error
 */
export const useUpdatePartner = () => {
  const { data: me, isLoading } = useGetPartnerQuery();
  const [handleUpdatePartner, handleUpdatePartnerResult] =
    usePutPartnerMutation();

  const handleSave = useCallback(
    async (newValue: Partial<PartnerUpdateType>) => {
      if (!me) {
        Logger.error(
          'Trying to save partner while getPartnerQuery is in error. Aborting.',
        );
        return Promise.resolve({ error: 'unknown partner' });
      }
      const partnerToSave: PartnerUpdateType = {
        companyVatNumber: newValue.companyVatNumber ?? me.companyVatNumber,
        address: newValue.address ?? me.address,
        stateRegion: newValue.stateRegion ?? me.stateRegion,
        town: newValue.town ?? me.town,
        zipCode: newValue.zipCode ?? me.zipCode,
        accountingEmail: newValue.accountingEmail ?? me.accountingEmail,
        firstName: newValue.firstName ?? me.firstName,
        lastName: newValue.lastName ?? me.lastName,
        phoneNumber: newValue.phoneNumber ?? me.phoneNumber,
        name: newValue.name ?? me.name,
      };

      return await handleUpdatePartner(partnerToSave);
    },
    [handleUpdatePartner, me],
  );

  return {
    isLoading: isLoading || handleUpdatePartnerResult.isLoading,
    savePartner: handleSave,
    error: handleUpdatePartnerResult.error,
  };
};
